import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  FiSearch,
  FiX,
  FiPlus,
  FiMinus,
  FiShoppingCart,
  FiPackage,
  FiCheckCircle,
  FiChevronDown,
  FiFolder,
  FiGrid,
  FiCheck,
  FiList,
} from 'react-icons/fi';
import axios from 'axios';
import API, { getImagenUrl } from 'shared/api';
import VisorImagen from 'shared/components/VisorImagen';
import { escalarEtiquetasExtra } from 'shared/etiquetaUtils';
import { aplicarTemaEmpresa } from 'shared/theme';
import useWindowSize from 'shared/hooks/useWindowSize';
import { fmtMonto } from 'shared/format';
import { useTasa } from './catalogo/hooks/useTasa';
import { useProductos } from './catalogo/hooks/useProductos';
import { S, SD, M, cssAnimations } from './catalogo/ClienteCatalogo.styles';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const MODELO_PALETTE = [
  '#1a73e8', '#0d8043', '#c5221f', '#e37400', '#7b1fa2',
  '#007b83', '#9334e6', '#188038', '#d93025', '#f9ab00',
];

const PALETTE = [
  '#1a73e8', '#0d8043', '#c5221f', '#e37400', '#7b1fa2',
  '#007b83', '#1967d2', '#9334e6', '#188038', '#9b3746',
];

function CarIcon({ size = 18, color = 'currentColor', style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <path d="M3 13l1.3-4.3A2 2 0 0 1 6.2 7h11.6a2 2 0 0 1 1.9 1.7L21 13" />
      <path d="M4 13h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1" />
      <path d="M5 18a2 2 0 1 1 4 0" />
      <path d="M15 18a2 2 0 1 1 4 0" />
      <path d="M7 18H5a1 1 0 0 1-1-1v-2M17 18h2a1 1 0 0 0 1-1v-2" />
    </svg>
  );
}

const _etKey = (ets) => (ets || []).map((e) => e.id).sort().join(',');
const _firmaCombo = (componentes) =>
  JSON.stringify(
    (componentes || []).map((x) => [
      x.codigo_producto,
      (x.etiquetas || []).map((e) => e.id).sort(),
    ])
  );

function MesaPedido({ slug }) {
  const { token } = useParams();
  const schemaName = (slug || '').replace(/-/g, '_');
  const tasaCambio = useTasa(schemaName);
  const { width: ww } = useWindowSize();
  const isMobile = ww < 768;

  const [config, setConfig] = useState(null);
  const [mesa, setMesa] = useState(null);
  const [sedeCodigo, setSedeCodigo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mesaError, setMesaError] = useState(null);

  const [combos, setCombos] = useState([]);

  const [carrito, setCarrito] = useState([]);
  const [nombre, setNombre] = useState('');
  const [enviado, setEnviado] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const [productoDetalle, setProductoDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [cantidadDetalle, setCantidadDetalle] = useState(1);
  const [etiquetasSeleccionadas, setEtiquetasSeleccionadas] = useState([]);
  const [galIdx, setGalIdx] = useState(0);
  const [visorOpen, setVisorOpen] = useState(null);
  const [hoveredCodigo, setHoveredCodigo] = useState(null);

  const [comboDetalle, setComboDetalle] = useState(null);
  const [comboSelEtiquetas, setComboSelEtiquetas] = useState({});
  const [agregandoCombo, setAgregandoCombo] = useState(null);
  const [comboImgRota, setComboImgRota] = useState({});

  const [showCarrito, setShowCarrito] = useState(false);
  const [showMobileDeptos, setShowMobileDeptos] = useState(false);
  const [mostrarSubgrupos, setMostrarSubgrupos] = useState(false);
  const vistaKey = `agorax_vista_catalogo_${schemaName}`;
  const [modoVista, setModoVista] = useState(() => localStorage.getItem(vistaKey) || 'grid');

  useEffect(() => {
    localStorage.setItem(vistaKey, modoVista);
  }, [modoVista, vistaKey]);

  useEffect(() => {
    localStorage.setItem('agorax_empresa_slug', slug);
  }, [slug]);

  // ── Carga inicial: config + mesa + combos ──
  useEffect(() => {
    let activo = true;
    setLoading(true);
    setMesaError(null);
    (async () => {
      try {
        const cfgRes = await axios.get(`${API_URL}/publico/empresa-config?slug=${slug}`);
        const cfg = cfgRes.data?.data;
        if (!activo) return;
        setConfig(cfg);
        aplicarTemaEmpresa(cfg);
        document.title = cfg?.nombre_app || cfg?.nombre || 'Agorax';

        const mesaRes = await API.get(`/publico/mesa/${token}`);
        const mesaData = mesaRes.data?.data?.mesa;
        if (!activo) return;
        setMesa(mesaData);
        setSedeCodigo(mesaData?.sede_codigo || '0101');

        axios
          .get(`${API_URL}/catalogo/combos?schema_name=${schemaName}`)
          .then((r) => activo && setCombos(r.data?.data || []))
          .catch(() => {});
      } catch (e) {
        if (!activo) return;
        setMesaError(
          e.response?.status === 404
            ? 'Esta mesa no está disponible.'
            : 'No se pudo cargar el menú. Intenta de nuevo.'
        );
      } finally {
        if (activo) setLoading(false);
      }
    })();
    return () => {
      activo = false;
    };
  }, [slug, token, schemaName]);

  const sede = useMemo(
    () => (sedeCodigo ? { codigo: sedeCodigo, nombre: '' } : null),
    [sedeCodigo]
  );

  const {
    departamentos,
    categorias,
    depSeleccionado,
    setDepSeleccionado,
    grupoSeleccionado,
    setGrupoSeleccionado,
    categoriaSeleccionada,
    seleccionarCategoria,
    busquedaInput,
    setBusquedaInput,
    setBusqueda,
    productos,
    cargandoProductos,
    hasMore,
    cargandoMas,
    cargarProductos,
    onSearchChange,
    seleccionarDepartamento,
    seleccionarGrupo,
    cursorUltimo,
  } = useProductos(sede, schemaName, null);

  const pva = config?.permitir_venta_agotados || false;

  const grupos = useMemo(() => {
    const map = new Map();
    departamentos.forEach((d) => {
      const nombre = d.grupo_nombre || d.grupo || 'Sin categoría';
      const codigo = d.grupo || '';
      const key = codigo || nombre;
      if (!map.has(key)) map.set(key, { codigo, nombre, depts: [] });
      map.get(key).depts.push(d);
    });
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        depts: [...g.depts].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es')),
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [departamentos]);

  // ── Detalle de producto ──
  const abrirDetalle = async (codigo) => {
    setCargandoDetalle(true);
    setGalIdx(0);
    try {
      const res = await axios.get(
        `${API_URL}/catalogo/producto/${codigo}?sede=${sedeCodigo || '0101'}&schema_name=${schemaName}`
      );
      if (res.data?.status === 'success') {
        const p = res.data.data;
        setProductoDetalle(p);
        const enCarrito = carrito.find((c) => c.codigo === p.codigo && !c.combo_id);
        const stockReal = p.stock != null ? p.stock : 0;
        const stockMax = pva ? Infinity : p.pesable ? stockReal * 1000 : stockReal;
        const defaultQty = p.pesable ? 500 : 1;
        setCantidadDetalle(
          enCarrito
            ? Math.min(enCarrito.cantidad, Math.max(stockMax, defaultQty))
            : Math.min(defaultQty, Math.max(stockMax, defaultQty))
        );
        if (enCarrito?.etiquetas) {
          setEtiquetasSeleccionadas(enCarrito.etiquetas.map((e) => e.id));
        } else {
          setEtiquetasSeleccionadas(
            (p.etiquetas || []).filter((e) => e.activa_por_defecto).map((e) => e.id)
          );
        }
      }
    } catch {
      /* ignore */
    } finally {
      setCargandoDetalle(false);
    }
  };

  const cerrarDetalle = () => {
    setProductoDetalle(null);
  };

  const ajusteCantidadDetalle = (delta) => {
    setCantidadDetalle((prev) => {
      const min = productoDetalle?.pesable ? 50 : 1;
      const max = productoDetalle?.pesable
        ? (productoDetalle.stock || 0) * 1000
        : productoDetalle.stock || 0;
      const next = prev + delta;
      if (next < min) return prev;
      if (!pva && productoDetalle && next > max) return prev;
      return next;
    });
  };

  // ── Carrito ──
  const agregarProducto = useCallback(
    (prod, cant, etiquetasSel) => {
      const etiq = etiquetasSel || [];
      const etiqIdsKey = _etKey(etiq);
      const etiqExtraUnit = etiq.reduce((s, e) => s + (e.precio || 0), 0);
      setCarrito((prev) => {
        const stockMax = pva ? Infinity : prod.pesable ? (prod.stock || 0) * 1000 : prod.stock || 0;
        const idx = prev.findIndex(
          (c) => !c.combo_id && c.codigo === prod.codigo && _etKey(c.etiquetas) === etiqIdsKey
        );
        if (idx >= 0) {
          return prev.map((c, i) => {
            if (i !== idx) return c;
            const nuevaCant = pva ? c.cantidad + cant : Math.min(c.cantidad + cant, stockMax);
            return { ...c, cantidad: nuevaCant, etiquetas: etiq, etiquetas_extra: etiqExtraUnit * nuevaCant };
          });
        }
        const nuevaCant = pva ? cant : Math.min(cant, stockMax);
        return [
          ...prev,
          {
            _key: `${prod.codigo}|${etiqIdsKey}`,
            codigo: prod.codigo,
            nombre: prod.descripcion || prod.nombre,
            precio: prod.precio || 0,
            precio_bs: tasaCambio ? (prod.precio || 0) * tasaCambio : 0,
            pesable: !!prod.pesable,
            cantidad: nuevaCant,
            stock: prod.stock,
            imagen_url: prod.imagen_url || prod.thumb_url || '',
            etiquetas: etiq,
            etiquetas_extra: etiqExtraUnit * nuevaCant,
            combo_id: null,
            componentes: null,
            combo_extra: 0,
          },
        ];
      });
    },
    [pva, tasaCambio]
  );

  const agregarCombo = useCallback(
    (combo, componentes, comboExtra) => {
      const firma = _firmaCombo(componentes);
      setCarrito((prev) => {
        const idx = prev.findIndex((c) => c.combo_id === combo.id && _firmaCombo(c.componentes) === firma);
        if (idx >= 0) {
          return prev.map((c, i) => (i === idx ? { ...c, cantidad: c.cantidad + 1 } : c));
        }
        return [
          ...prev,
          {
            _key: `combo_${combo.id}|${firma}`,
            codigo: `combo_${combo.id}`,
            nombre: combo.nombre,
            precio: combo.precio || 0,
            precio_bs: tasaCambio ? (combo.precio || 0) * tasaCambio : 0,
            pesable: false,
            cantidad: 1,
            stock: 999999,
            imagen_url: combo.imagen_url || combo.thumb_url || '',
            etiquetas: [],
            etiquetas_extra: 0,
            combo_id: combo.id,
            componentes,
            combo_extra: comboExtra || 0,
          },
        ];
      });
    },
    [tasaCambio]
  );

  const cambiarCantidad = useCallback(
    (_key, delta) => {
      setCarrito((prev) =>
        prev
          .map((c) => {
            if (c._key !== _key) return c;
            const stockMax =
              pva || c.combo_id ? Infinity : c.pesable ? (c.stock || 0) * 1000 : c.stock || 0;
            const nueva = Math.min(Math.max(0, c.cantidad + delta), stockMax);
            return { ...c, cantidad: nueva, etiquetas_extra: escalarEtiquetasExtra(c, nueva) };
          })
          .filter((c) => c.cantidad > 0)
      );
    },
    [pva]
  );

  const eliminarDelCarrito = useCallback((_key) => {
    setCarrito((prev) => prev.filter((c) => c._key !== _key));
  }, []);

  const totalCarrito = useMemo(
    () =>
      carrito.reduce((s, c) => {
        const base = c.pesable ? (c.precio * c.cantidad) / 1000 : c.precio * c.cantidad;
        return s + base + (c.etiquetas_extra || 0) + (c.combo_extra || 0) * c.cantidad;
      }, 0),
    [carrito]
  );

  const confirmar = async () => {
    if (carrito.length === 0 || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const items = carrito.map((c) => ({
        codigo: c.codigo,
        nombre: c.nombre,
        cantidad: c.cantidad,
        precio_unitario: c.precio || 0,
        pesable: c.pesable,
        etiquetas: (c.etiquetas || []).map((e) => ({ id: e.id, nombre: e.nombre, precio: e.precio || 0 })),
        etiquetas_extra: c.etiquetas_extra || 0,
        combo_id: c.combo_id || null,
        componentes: c.componentes || null,
        combo_extra: c.combo_extra || 0,
      }));
      const r = await API.post(`/publico/mesa/${token}/pedido`, {
        items,
        cliente_nombre: nombre.trim() || null,
      });
      if (r.data?.status === 'success') {
        setEnviado(r.data.data);
        setCarrito([]);
        setNombre('');
        setShowCarrito(false);
      } else {
        setError(r.data?.message || 'No se pudo enviar el pedido.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo enviar el pedido.');
    } finally {
      setEnviando(false);
    }
  };

  // ── Combos ──
  const instanciasCombo = (items) => {
    const out = [];
    (items || []).forEach((it) => {
      const cant = Math.max(1, parseInt(it.cantidad, 10) || 1);
      for (let idx = 0; idx < cant; idx++) out.push({ ...it, _instancia: idx });
    });
    return out;
  };

  const abrirComboDetalle = (combo) => {
    const sel = {};
    instanciasCombo(combo.items).forEach((it) => {
      sel[`${it.codigo_producto}#${it._instancia}`] = (it.etiquetas || []).map((e) => e.id);
    });
    setComboSelEtiquetas(sel);
    setComboDetalle(combo);
  };

  const cerrarComboDetalle = () => {
    setComboDetalle(null);
    setComboSelEtiquetas({});
  };

  const toggleEtiquetaCombo = (codigoProducto, instanciaIdx, et) => {
    const key = `${codigoProducto}#${instanciaIdx}`;
    setComboSelEtiquetas((prev) => {
      const actual = prev[key] || [];
      const ya = actual.includes(et.id);
      return { ...prev, [key]: ya ? actual.filter((id) => id !== et.id) : [...actual, et.id] };
    });
  };

  const agregarComboPersonalizado = () => {
    if (!comboDetalle) return;
    setAgregandoCombo(comboDetalle.id);
    let extra = 0;
    const componentes = instanciasCombo(comboDetalle.items).map((it) => {
      const incluidos = new Set((it.etiquetas || []).map((e) => e.id));
      const seleccionados = comboSelEtiquetas[`${it.codigo_producto}#${it._instancia}`] || [];
      const etiquetas = (it.etiquetas_disponibles || [])
        .filter((e) => seleccionados.includes(e.id))
        .map((e) => ({ id: e.id, nombre: e.nombre, precio: e.precio || 0 }));
      (it.etiquetas_disponibles || []).forEach((e) => {
        if (!incluidos.has(e.id) && seleccionados.includes(e.id)) extra += e.precio || 0;
      });
      return { codigo_producto: it.codigo_producto, nombre: it.nombre, etiquetas };
    });
    agregarCombo(comboDetalle, componentes, extra);
    cerrarComboDetalle();
    setTimeout(() => setAgregandoCombo(null), 500);
  };

  // ── Estados de pantalla completa ──
  if (loading) {
    return (
      <div style={SC.center}>
        <div style={S.spinner} />
        <div style={{ marginTop: 12, color: 'var(--color-text-secondary)', fontSize: 14 }}>
          Cargando menú...
        </div>
        <style>{cssAnimations}</style>
      </div>
    );
  }

  if (mesaError && !mesa) {
    return (
      <div style={SC.center}>
        <div style={{ fontSize: 16, color: 'var(--color-text-secondary)', textAlign: 'center', padding: 24 }}>
          {mesaError}
        </div>
        <style>{cssAnimations}</style>
      </div>
    );
  }

  if (enviado) {
    return (
      <div style={SC.center}>
        <div style={{ textAlign: 'center', padding: 24, maxWidth: 360 }}>
          <FiCheckCircle size={64} color="var(--color-success)" />
          <h2 style={{ color: 'var(--color-primary)', margin: '16px 0 8px', fontSize: 22 }}>
            Pedido enviado
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 15, lineHeight: 1.5 }}>
            Tu pedido #{enviado.ticket_numero} fue enviado a la mesa{' '}
            <strong>{mesa?.nombre || mesa?.codigo}</strong>. Un mesero lo atenderá en breve.
          </p>
          <button
            onClick={() => {
              setEnviado(null);
              setError(null);
            }}
            style={S.confirmarPedidoBtn}
          >
            Hacer otro pedido
          </button>
        </div>
        <style>{cssAnimations}</style>
      </div>
    );
  }

  const logoUrl = config?.logo_url ? getImagenUrl(config.logo_url) : null;

  return (
    <div style={SC.wrap}>
      <style>{cssAnimations}</style>

      {/* Header de la empresa */}
      <header style={SC.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {logoUrl ? (
            <img src={logoUrl} alt={config?.nombre || 'Logo'} style={SC.logo} />
          ) : (
            <FiPackage size={22} color="var(--color-surface)" />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-surface)', letterSpacing: '-0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {config?.nombre_app || config?.nombre || 'Menú'}
            </div>
            <div style={{ fontSize: 12, opacity: 0.85, color: 'var(--color-surface)' }}>
              Mesa {mesa?.nombre || mesa?.codigo}
            </div>
          </div>
          {isMobile && (
            <button
              onClick={() => setShowCarrito(true)}
              style={SC.cartBtn}
              aria-label="Ver pedido"
            >
              <FiShoppingCart size={20} />
              {carrito.length > 0 && <span style={SD.fabBadge}>{carrito.length}</span>}
            </button>
          )}
        </div>
      </header>

      {error && (
        <div style={SC.errorBanner}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 700, marginLeft: 8 }}
          >
            OK
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Sidebar de departamentos - Desktop */}
        {!isMobile && (
          <div style={SD.sidebar}>
            {categorias.length > 0 && (
              <div style={{ paddingTop: '16px', borderBottom: '1px solid var(--color-border)', paddingBottom: '12px' }}>
                <div style={{ ...SD.sidebarTitle, padding: '4px 12px 10px' }}>Modelos</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', padding: '0 12px 0' }}>
                  <button
                    onClick={() => seleccionarCategoria(categoriaSeleccionada)}
                    style={{
                      ...SD.sidebarItem,
                      backgroundColor: !categoriaSeleccionada ? 'var(--color-bg-hover)' : 'transparent',
                      color: !categoriaSeleccionada ? 'var(--color-secondary)' : 'var(--color-text)',
                      fontWeight: !categoriaSeleccionada ? 600 : 400,
                      fontSize: '12px',
                    }}
                  >
                    <div style={{ width: '26px', height: '26px', borderRadius: '8px', background: '#e8eaed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <FiGrid size={13} color="#5f6368" />
                    </div>
                    <span style={{ flex: 1 }}>Todos los modelos</span>
                    {!categoriaSeleccionada && <FiCheckCircle size={14} color="var(--color-secondary)" />}
                  </button>
                  {categorias.map((c, idx) => {
                    const sel = categoriaSeleccionada === c.categoria;
                    const color = MODELO_PALETTE[idx % MODELO_PALETTE.length];
                    return (
                      <button
                        key={c.categoria}
                        onClick={() => seleccionarCategoria(c.categoria)}
                        style={{
                          ...SD.sidebarItem,
                          backgroundColor: sel ? `${color}10` : 'transparent',
                          color: sel ? color : 'var(--color-text)',
                          fontWeight: sel ? 600 : 400,
                          fontSize: '12px',
                          borderLeft: sel ? `3px solid ${color}` : '3px solid transparent',
                        }}
                      >
                        <div style={{ width: '26px', height: '26px', borderRadius: '8px', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <CarIcon size={14} color={color} />
                        </div>
                        <span style={{ flex: 1, wordBreak: 'break-word', lineHeight: 1.3 }}>{c.categoria}</span>
                        <span style={{ fontSize: '10px', fontWeight: 600, color: sel ? color : 'var(--color-text-muted)', background: sel ? `${color}18` : '#f1f3f4', padding: '1px 7px', borderRadius: '10px' }}>
                          {c.total_productos}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ ...SD.sidebarTitle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '12px' }}>
              <span>Categorías</span>
              <button
                onClick={() => setMostrarSubgrupos((v) => !v)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--color-secondary)',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: 0,
                }}
              >
                {mostrarSubgrupos ? 'Ocultar subgrupos' : 'Ver subgrupos'}
                <FiChevronDown
                  size={13}
                  style={{ transform: mostrarSubgrupos ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                />
              </button>
            </div>
            <div style={SD.sidebarList}>
              <button
                onClick={() => { setDepSeleccionado(null); setGrupoSeleccionado(null); }}
                style={{
                  ...SD.sidebarItem,
                  backgroundColor: !depSeleccionado && grupoSeleccionado == null ? 'var(--color-secondary)' : 'transparent',
                  color: !depSeleccionado && grupoSeleccionado == null ? 'var(--color-surface)' : 'var(--color-primary)',
                  fontWeight: !depSeleccionado && grupoSeleccionado == null ? 700 : 500,
                }}
              >
                <FiGrid size={16} />
                <span>Todos los productos</span>
              </button>
              {grupos.map((g, idx) => {
                const color = PALETTE[idx % PALETTE.length];
                const grupoActivo = grupoSeleccionado != null && grupoSeleccionado === g.codigo;
                return (
                  <div key={g.nombre} style={{ marginTop: '4px' }}>
                    <button
                      onClick={() => {
                        setDepSeleccionado(null);
                        seleccionarGrupo(g.codigo);
                      }}
                      style={{
                        ...SD.sidebarItem,
                        fontWeight: 600,
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        color,
                        backgroundColor: grupoActivo ? `${color}18` : 'transparent',
                        justifyContent: 'space-between',
                        borderLeft: `3px solid ${color}`,
                        paddingLeft: '12px',
                      }}
                    >
                      <span>{g.nombre}</span>
                      <span style={{ fontSize: '10px', fontWeight: 600, background: `${color}18`, color, padding: '1px 7px', borderRadius: '10px' }}>
                        {g.depts.length}
                      </span>
                    </button>
                    {mostrarSubgrupos &&
                      g.depts.map((d) => (
                        <button
                          key={d.codigo}
                          onClick={() => { seleccionarDepartamento(d.codigo); setGrupoSeleccionado(null); }}
                          style={{
                            ...SD.sidebarItem,
                            paddingLeft: '28px',
                            backgroundColor: depSeleccionado === d.codigo ? 'var(--color-bg-hover)' : 'transparent',
                            color: depSeleccionado === d.codigo ? 'var(--color-secondary)' : 'var(--color-text)',
                            fontWeight: depSeleccionado === d.codigo ? 600 : 400,
                            borderLeft: depSeleccionado === d.codigo ? '3px solid var(--color-secondary)' : '3px solid transparent',
                          }}
                        >
                          <span>{d.nombre}</span>
                        </button>
                      ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Contenido principal */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          <div style={S.catHeader}>
            <div style={S.searchRow}>
              <div style={S.searchBox}>
                <FiSearch size={16} color="var(--color-text-secondary)" />
                <input
                  placeholder="Buscar productos..."
                  value={busquedaInput}
                  onChange={(e) => onSearchChange(e.target.value)}
                  style={S.searchInput}
                />
                {busquedaInput && (
                  <button
                    onClick={() => {
                      setBusquedaInput('');
                      setBusqueda('');
                    }}
                    style={S.searchClear}
                    aria-label="Limpiar búsqueda"
                  >
                    <FiX size={16} />
                  </button>
                )}
              </div>
              <div style={S.vistaToggle}>
                <button
                  onClick={() => setModoVista('grid')}
                  title="Vista cuadrícula"
                  style={modoVista === 'grid' ? S.vistaToggleBtnActive : S.vistaToggleBtn}
                >
                  <FiGrid size={16} />
                </button>
                <button
                  onClick={() => setModoVista('list')}
                  title="Vista lista"
                  style={modoVista === 'list' ? S.vistaToggleBtnActive : S.vistaToggleBtn}
                >
                  <FiList size={16} />
                </button>
              </div>
            </div>

            <div style={S.sedeInfo}>
              <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>Mesa:</span>
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-primary)', marginLeft: 6 }}>
                {mesa?.nombre || mesa?.codigo}
              </span>
            </div>

            {isMobile && (
              <div style={{ padding: '0 0 10px' }}>
                <button
                  onClick={() => setShowMobileDeptos(true)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: '1px solid var(--color-border)',
                    backgroundColor: 'var(--color-surface)',
                    color: 'var(--color-text)',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  }}
                >
                  <FiFolder size={18} color="var(--color-secondary)" />
                  <span style={{ flex: 1, textAlign: 'left', fontWeight: 500 }}>
                    {depSeleccionado
                      ? departamentos.find((d) => d.codigo === depSeleccionado)?.nombre || 'Categorías'
                      : grupoSeleccionado != null
                        ? grupos.find((g) => g.codigo === grupoSeleccionado)?.nombre || 'Categorías'
                        : 'Todos los productos'}
                  </span>
                  <FiChevronDown size={16} color="var(--color-text-secondary)" />
                </button>
              </div>
            )}

            {(depSeleccionado || grupoSeleccionado != null || categoriaSeleccionada) && (
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', padding: '0 0 10px' }}>
                {depSeleccionado && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '5px 12px',
                      borderRadius: '18px',
                      background: 'var(--color-secondary)',
                      color: 'var(--color-surface)',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    <FiFolder size={12} />
                    {departamentos.find((d) => d.codigo === depSeleccionado)?.nombre}
                    <button
                      onClick={() => setDepSeleccionado(null)}
                      style={{ border: 'none', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--color-surface)' }}
                    >
                      <FiX size={10} />
                    </button>
                  </span>
                )}
                {grupoSeleccionado != null && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '5px 12px',
                      borderRadius: '18px',
                      background: 'var(--color-secondary)',
                      color: 'var(--color-surface)',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    <FiFolder size={12} />
                    {grupos.find((g) => g.codigo === grupoSeleccionado)?.nombre || grupoSeleccionado}
                    <button
                      onClick={() => setGrupoSeleccionado(null)}
                      style={{ border: 'none', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--color-surface)' }}
                    >
                      <FiX size={10} />
                    </button>
                  </span>
                )}
                {categoriaSeleccionada && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '5px 12px',
                      borderRadius: '18px',
                      background: 'var(--color-bg-hover)',
                      color: 'var(--color-text)',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                  >
                    {categoriaSeleccionada}
                    <button
                      onClick={() => seleccionarCategoria(null)}
                      style={{ border: 'none', background: 'rgba(0,0,0,0.1)', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
                    >
                      <FiX size={10} />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>

          <main style={S.grid}>
            {combos.length > 0 && !depSeleccionado && grupoSeleccionado == null && !categoriaSeleccionada && !busquedaInput && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <FiGrid size={16} color="var(--color-secondary)" />
                  <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--color-primary)' }}>Combos</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '12px' }}>
                  {combos.map((combo) => {
                    const comboTumb = combo.thumb_url || combo.imagen_url;
                    const comboImgSrc = getImagenUrl(comboTumb);
                    const rota = comboImgRota[combo.id];
                    return (
                      <div
                        key={combo.id}
                        onClick={() => abrirComboDetalle(combo)}
                        style={{
                          backgroundColor: 'var(--color-surface)',
                          borderRadius: '16px',
                          border: '1px solid #F0F0F0',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          fontFamily: 'inherit',
                          width: '100%',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          transition: 'box-shadow 0.15s, transform 0.15s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.10)')}
                        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)')}
                      >
                        <div style={{ width: '100%', height: '110px', backgroundColor: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                          {comboImgSrc && !rota ? (
                            <img
                              src={comboImgSrc}
                              alt={combo.nombre}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={() => setComboImgRota((p) => ({ ...p, [combo.id]: true }))}
                            />
                          ) : (
                            <FiPackage size={34} color="var(--color-secondary)" />
                          )}
                          {combo.ahorro > 0 && (
                            <span style={{ position: 'absolute', top: 8, left: 8, fontSize: '11px', fontWeight: 700, color: '#137333', background: '#e6f4ea', padding: '3px 8px', borderRadius: '20px' }}>
                              Ahorras ${fmtMonto(combo.ahorro)}
                            </span>
                          )}
                        </div>
                        <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, textAlign: 'left' }}>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-primary)', lineHeight: 1.3, wordBreak: 'break-word' }}>
                            {combo.nombre}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                            {combo.precio_oculto ? (
                              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text-muted)' }}>Precio oculto hasta tu aprobación</span>
                            ) : (
                              <>
                                <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--color-secondary)' }}>
                                  ${fmtMonto(combo.precio || 0)}
                                </span>
                                {config?.mostrar_precio_bs !== false && Number.isFinite(tasaCambio) && tasaCambio > 0 && (
                                  <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                                    Bs {fmtMonto((combo.precio || 0) * tasaCambio)}
                                  </span>
                                )}
                                {combo.precio_sin_descuento > 0 && combo.precio_sin_descuento > combo.precio && (
                                  <span style={{ fontSize: '11px', color: 'var(--color-text-secondary)', textDecoration: 'line-through' }}>
                                    ${fmtMonto(combo.precio_sin_descuento || 0)}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          {combo.items.length > 0 && (
                            <div style={{ fontSize: '10px', color: 'var(--color-text-muted)', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
                              {combo.items.map((it) => `${it.cantidad}× ${it.nombre}`).join(' · ')}
                            </div>
                          )}
                          <span style={{ marginTop: 'auto', width: '100%', padding: '8px 0', borderRadius: '9px', background: 'var(--color-secondary)', color: '#fff', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            <FiPlus size={13} /> Personalizar
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {cargandoProductos ? (
              <div style={S.loadingState}>
                <div style={S.spinner} />
                <span style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>Cargando productos...</span>
              </div>
            ) : productos.length === 0 ? (
              <div style={S.emptyState}>
                <FiPackage size={48} color="var(--color-text-muted)" />
                <span style={{ color: 'var(--color-text-secondary)', fontSize: '14px', marginTop: 12 }}>
                  No hay productos que coincidan.
                </span>
              </div>
            ) : modoVista === 'list' ? (
              <div style={S.productList}>
                {productos.map((prod) => {
                  const imagenes = prod.imagenes || [];
                  const mediaPrincipal = imagenes.length > 0 ? imagenes[0] : null;
                  const currentUrl = mediaPrincipal ? mediaPrincipal.url : prod.thumb_url || prod.imagen_url;
                  const isVideo = mediaPrincipal?.tipo === 'video';
                  const stockOk = prod.stock == null || prod.stock > 0;
                  const interactivo = pva || stockOk;
                  return (
                    <button
                      key={prod.codigo}
                      onClick={() => interactivo && abrirDetalle(prod.codigo)}
                      disabled={!interactivo}
                      style={{ ...S.productListItem, opacity: interactivo ? 1 : 0.5, cursor: interactivo ? 'pointer' : 'default' }}
                    >
                      <div style={S.productListImg}>
                        {isVideo ? (
                          <video src={getImagenUrl(currentUrl)} autoPlay muted loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : getImagenUrl(currentUrl) ? (
                          <img src={getImagenUrl(currentUrl)} alt={prod.descripcion} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => (e.currentTarget.style.display = 'none')} />
                        ) : (
                          <FiPackage size={24} color="var(--color-secondary)" />
                        )}
                        {prod.pesable && (
                          <span style={{ position: 'absolute', top: '2px', right: '2px', fontSize: '7px', fontWeight: 600, backgroundColor: '#fef7e0', color: '#b06000', padding: '1px 4px', borderRadius: '4px' }}>
                            Pesable
                          </span>
                        )}
                      </div>
                      <div style={S.productListInfo}>
                        <div style={S.productListName}>{prod.descripcion}</div>
                        {prod.descripcion_larga && <div style={S.productListDesc}>{prod.descripcion_larga}</div>}
                      </div>
                      <div style={S.productListActions}>
                        {prod.precio_oculto ? (
                          <div style={S.productListPriceOculto}>Precio oculto</div>
                        ) : (
                          <>
                            <div style={S.productListPrice}>${fmtMonto(prod.precio || 0)}</div>
                            {config?.mostrar_precio_bs !== false && Number.isFinite(tasaCambio) && tasaCambio > 0 && (
                              <div style={S.productListPriceBs}>Bs {fmtMonto((prod.precio || 0) * tasaCambio)}</div>
                            )}
                          </>
                        )}
                        {!pva && !prod.precio_oculto && prod.stock != null && (
                          <div style={{ ...S.productListStock, color: prod.stock > 20 ? '#0d904f' : prod.stock > 0 ? '#ea8600' : 'var(--color-danger)' }}>
                            {prod.stock > 20 ? 'Disponible' : prod.stock > 0 ? `Quedan ${prod.stock}` : 'Agotado'}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div style={{ ...S.productGrid, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                {productos.map((prod) => {
                  const imagenes = prod.imagenes || [];
                  const mediaPrincipal = imagenes.length > 0 ? imagenes[0] : null;
                  const currentUrl = mediaPrincipal ? mediaPrincipal.url : prod.thumb_url || prod.imagen_url;
                  const isVideo = mediaPrincipal?.tipo === 'video';
                  const isHovered = hoveredCodigo === prod.codigo;
                  const stockOk = prod.stock == null || prod.stock > 0;
                  const interactivo = pva || stockOk;
                  return (
                    <button
                      key={prod.codigo}
                      onClick={() => interactivo && abrirDetalle(prod.codigo)}
                      disabled={!interactivo}
                      onMouseEnter={() => setHoveredCodigo(prod.codigo)}
                      onMouseLeave={() => setHoveredCodigo(null)}
                      style={{
                        ...S.productCard,
                        opacity: interactivo ? 1 : 0.5,
                        cursor: interactivo ? 'pointer' : 'default',
                        borderColor: isHovered && interactivo ? 'var(--color-secondary)' : '#F0F0F0',
                        boxShadow: isHovered && interactivo ? '0 8px 24px rgba(11,26,74,0.12)' : 'none',
                        transform: isHovered && interactivo ? 'translateY(-3px)' : 'none',
                      }}
                    >
                      <div style={S.productImage}>
                        {isVideo ? (
                          <video src={getImagenUrl(currentUrl)} autoPlay muted loop playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : getImagenUrl(currentUrl) ? (
                          <img src={getImagenUrl(currentUrl)} alt={prod.descripcion} style={S.productImg} onError={(e) => (e.currentTarget.style.display = 'none')} />
                        ) : (
                          <FiPackage size={28} color="var(--color-secondary)" />
                        )}
                      </div>
                      <div style={S.productInfo}>
                        <div style={S.productName}>{prod.descripcion}</div>
                        {prod.descripcion_larga && <div style={S.productDesc}>{prod.descripcion_larga}</div>}
                        {prod.precio_oculto ? (
                          <div style={S.productPriceOculto}>Precio oculto</div>
                        ) : (
                          <>
                            <div style={S.productPrice}>${fmtMonto(prod.precio || 0)}</div>
                            {config?.mostrar_precio_bs !== false && Number.isFinite(tasaCambio) && tasaCambio > 0 && (
                              <div style={S.productPriceBs}>Bs {fmtMonto((prod.precio || 0) * tasaCambio)}</div>
                            )}
                          </>
                        )}
                        {!pva && !prod.precio_oculto && prod.stock != null && (
                          <div style={{ ...S.stockBadge, color: prod.stock > 20 ? '#0d904f' : prod.stock > 0 ? '#ea8600' : 'var(--color-danger)' }}>
                            {prod.stock > 20 ? 'Disponible' : prod.stock > 0 ? `Quedan ${prod.stock}` : 'Agotado'}
                          </div>
                        )}
                        {prod.referencia && (
                          <div style={{ fontSize: '10px', color: 'var(--color-text-muted)', marginTop: '2px' }}>Ref: {prod.referencia}</div>
                        )}
                      </div>
                      {prod.pesable && <span style={S.pesableTag}>Pesable</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {hasMore && !cargandoProductos && (
              <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
                {cargandoMas ? (
                  <div style={S.spinnerSmall} />
                ) : (
                  <button
                    onClick={() => cargarProductos(false, cursorUltimo)}
                    style={{
                      padding: '10px 22px',
                      borderRadius: '20px',
                      border: '1.5px solid var(--color-border)',
                      background: 'var(--color-surface)',
                      color: 'var(--color-text-secondary)',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Ver más productos
                  </button>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* FAB carrito - Desktop */}
      {!isMobile && carrito.length > 0 && (
        <button onClick={() => setShowCarrito(true)} style={SD.fab} aria-label="Ver pedido">
          <FiShoppingCart size={22} color="var(--color-surface)" />
          <span style={SD.fabBadge}>{carrito.length}</span>
        </button>
      )}

      {/* Carrito flotante - Móvil */}
      {isMobile && carrito.length > 0 && !showCarrito && (
        <div style={S.cartFloat} onClick={() => setShowCarrito(true)}>
          <FiShoppingCart size={20} color="var(--color-surface)" />
          <span style={S.cartCount}>{carrito.length}</span>
          <span style={S.cartTotal}>${fmtMonto(totalCarrito)}</span>
        </div>
      )}

      {/* Detalle de producto */}
      {productoDetalle && (
        <div style={{ ...S.detailOverlay, alignItems: isMobile ? 'flex-end' : 'center' }} onClick={cerrarDetalle}>
          <div
            style={{ ...S.detailModal, borderRadius: isMobile ? '20px 20px 0 0' : '20px', maxWidth: isMobile ? '480px' : '520px', maxHeight: isMobile ? '90vh' : '85vh' }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button onClick={cerrarDetalle} style={S.detailClose} aria-label="Cerrar">
              <FiX size={22} />
            </button>

            {cargandoDetalle ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={S.spinner} />
              </div>
            ) : (
              <>
                <div style={S.detailImage}>
                  {productoDetalle.imagenes?.length > 0 && productoDetalle.imagenes[galIdx].tipo !== 'video' && (
                    <div onClick={() => setVisorOpen({ images: productoDetalle.imagenes, startIdx: galIdx })} style={{ cursor: 'pointer', width: '100%', height: '100%' }}>
                      <img src={getImagenUrl(productoDetalle.imagenes[galIdx].url)} alt={productoDetalle.descripcion} style={S.detailImg} onError={(e) => (e.currentTarget.style.display = 'none')} />
                    </div>
                  )}
                  {productoDetalle.imagenes?.length > 0 && productoDetalle.imagenes[galIdx].tipo === 'video' && (
                    <video key={productoDetalle.imagenes[galIdx].url} src={getImagenUrl(productoDetalle.imagenes[galIdx].url)} controls playsInline style={{ width: '100%', minHeight: '200px', background: '#000', borderRadius: '12px' }} />
                  )}
                  {!productoDetalle.imagenes?.length && getImagenUrl(productoDetalle.imagen_url) && (
                    <div onClick={() => setVisorOpen({ images: [{ url: productoDetalle.imagen_url }], startIdx: 0 })} style={{ cursor: 'pointer', width: '100%', height: '100%' }}>
                      <img src={getImagenUrl(productoDetalle.thumb_url || productoDetalle.imagen_url)} alt={productoDetalle.descripcion} style={S.detailImg} onError={(e) => (e.currentTarget.style.display = 'none')} />
                    </div>
                  )}
                  {!productoDetalle.imagenes?.length && !getImagenUrl(productoDetalle.imagen_url) && (
                    <FiPackage size={64} color="var(--color-secondary)" />
                  )}
                </div>

                {productoDetalle.imagenes?.length > 1 && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {productoDetalle.imagenes.map((img, idx) => (
                      <button key={idx} onClick={() => setGalIdx(idx)} style={{ width: '56px', height: '56px', borderRadius: '10px', overflow: 'hidden', flexShrink: 0, border: galIdx === idx ? '2px solid var(--color-primary)' : '2px solid transparent', padding: 0, cursor: 'pointer', background: 'var(--color-bg)' }}>
                        {img.tipo === 'video' ? (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a1a' }}>
                            <span style={{ color: '#fff', fontSize: '18px' }}>▶</span>
                          </div>
                        ) : (
                          <img src={getImagenUrl(img.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => (e.currentTarget.style.display = 'none')} />
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <div style={S.detailBody}>
                  <h2 style={S.detailName}>{productoDetalle.descripcion}</h2>
                  {productoDetalle.descripcion_completa && productoDetalle.descripcion_completa !== productoDetalle.descripcion && (
                    <p style={S.detailDesc}>{productoDetalle.descripcion_completa}</p>
                  )}
                  {productoDetalle.descripcion_corta && (
                    <p style={S.detailDesc}>{productoDetalle.descripcion_corta}</p>
                  )}

                  <div style={S.detailPrices}>
                    {productoDetalle.precio_oculto ? (
                      <span style={S.detailPrecioOculto}>Precio oculto hasta que te aprueben</span>
                    ) : (
                      <>
                        <span style={S.detailPriceUsd}>${fmtMonto(productoDetalle.precio || 0)}</span>
                        {config?.mostrar_precio_bs !== false && Number.isFinite(tasaCambio) && tasaCambio > 0 && (
                          <span style={S.detailPriceBs}>Bs {fmtMonto((productoDetalle.precio || 0) * tasaCambio)}</span>
                        )}
                      </>
                    )}
                  </div>

                  <div style={S.detailTags}>
                    {productoDetalle.pesable && <span style={S.tagYellow}>Vendido por peso</span>}
                    {!pva && !productoDetalle.precio_oculto && productoDetalle.stock != null && (
                      <span
                        style={{
                          ...S.tagGreen,
                          backgroundColor: productoDetalle.stock > 20 ? '#e8f5e9' : productoDetalle.stock > 0 ? '#fff3e0' : 'var(--color-danger-light)',
                          color: productoDetalle.stock > 20 ? '#0d904f' : productoDetalle.stock > 0 ? '#ea8600' : 'var(--color-danger)',
                        }}
                      >
                        {productoDetalle.stock > 20 ? `${productoDetalle.stock} en stock` : productoDetalle.stock > 0 ? `Solo ${productoDetalle.stock} disponibles` : 'Agotado'}
                      </span>
                    )}
                  </div>

                  <div style={S.detailMeta}>
                    {productoDetalle.marca && (
                      <div style={S.metaItem}><span style={S.metaLabel}>Marca</span><span style={S.metaValue}>{productoDetalle.marca}</span></div>
                    )}
                    {productoDetalle.departamento && (
                      <div style={S.metaItem}><span style={S.metaLabel}>Departamento</span><span style={S.metaValue}>{productoDetalle.departamento}</span></div>
                    )}
                    {productoDetalle.grupo && (
                      <div style={S.metaItem}><span style={S.metaLabel}>Grupo</span><span style={S.metaValue}>{productoDetalle.grupo}</span></div>
                    )}
                    {productoDetalle.referencia && (
                      <div style={S.metaItem}><span style={S.metaLabel}>Referencia</span><span style={S.metaValue}>{productoDetalle.referencia}</span></div>
                    )}
                  </div>

                  <div style={S.detailActions}>
                    <div style={S.cantSelector}>
                      <button onClick={() => ajusteCantidadDetalle(productoDetalle.pesable ? -50 : -1)} disabled={cantidadDetalle <= (productoDetalle.pesable ? 50 : 1)} style={S.cantBtn} aria-label="Disminuir cantidad">
                        <FiMinus size={16} />
                      </button>
                      <span style={S.cantValue}>
                        {productoDetalle.pesable ? (cantidadDetalle / 1000).toFixed(2) + ' kg' : cantidadDetalle}
                      </span>
                      <button
                        onClick={() => ajusteCantidadDetalle(productoDetalle.pesable ? 50 : 1)}
                        disabled={!pva && cantidadDetalle >= (productoDetalle.pesable ? (productoDetalle.stock || 0) * 1000 : (productoDetalle.stock || 0))}
                        style={S.cantBtn}
                        aria-label="Aumentar cantidad"
                      >
                        <FiPlus size={16} />
                      </button>
                    </div>

                    {(productoDetalle.etiquetas || []).filter((e) => e.activo !== false).length > 0 && (
                      <div style={{ marginTop: '4px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Personalizar
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {productoDetalle.etiquetas.filter((e) => e.activo !== false).map((et) => {
                            const seleccionada = etiquetasSeleccionadas.includes(et.id);
                            return (
                              <div
                                key={et.id}
                                onClick={() => setEtiquetasSeleccionadas((prev) => (seleccionada ? prev.filter((id) => id !== et.id) : [...prev, et.id]))}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '10px 12px',
                                  borderRadius: '12px',
                                  cursor: 'pointer',
                                  backgroundColor: seleccionada ? 'var(--color-secondary)' : 'var(--color-bg)',
                                  border: seleccionada ? '2px solid var(--color-secondary)' : '2px solid var(--color-border)',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div style={{ width: '20px', height: '20px', borderRadius: '6px', border: seleccionada ? '2px solid #fff' : '2px solid var(--color-text-secondary)', backgroundColor: seleccionada ? 'rgba(255,255,255,0.25)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {seleccionada && <FiCheck size={12} color="#fff" />}
                                  </div>
                                  <span style={{ fontSize: '13px', fontWeight: seleccionada ? 600 : 400, color: seleccionada ? '#fff' : 'var(--color-text)' }}>
                                    {et.nombre}
                                  </span>
                                </div>
                                {et.precio > 0 && (
                                  <span style={{ fontSize: '13px', fontWeight: 700, color: seleccionada ? '#fff' : 'var(--color-secondary)' }}>
                                    +${fmtMonto(et.precio)}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => {
                        const etiqObj = (productoDetalle.etiquetas || []).filter((e) => etiquetasSeleccionadas.includes(e.id));
                        agregarProducto(productoDetalle, cantidadDetalle, etiqObj);
                        cerrarDetalle();
                      }}
                      disabled={!pva && (!productoDetalle.stock || productoDetalle.stock <= 0)}
                      style={{ ...S.addBtn, opacity: !pva && (!productoDetalle.stock || productoDetalle.stock <= 0) ? 0.5 : 1, cursor: !pva && (!productoDetalle.stock || productoDetalle.stock <= 0) ? 'default' : 'pointer' }}
                    >
                      <FiShoppingCart size={18} />
                      {!pva && (!productoDetalle.stock || productoDetalle.stock <= 0) ? 'Producto agotado' : 'Agregar al carrito'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Detalle de combo */}
      {comboDetalle && (() => {
        const instancias = instanciasCombo(comboDetalle.items);
        const extra = instancias.reduce((s, it) => {
          const incluidos = new Set((it.etiquetas || []).map((e) => e.id));
          const seleccionados = comboSelEtiquetas[`${it.codigo_producto}#${it._instancia}`] || [];
          const suma = (it.etiquetas_disponibles || []).reduce((t, e) => {
            if (!incluidos.has(e.id) && seleccionados.includes(e.id)) return t + (e.precio || 0);
            return t;
          }, 0);
          return s + suma;
        }, 0);
        const totalCombo = (comboDetalle.precio || 0) + extra;
        return (
          <div style={{ ...S.detailOverlay, alignItems: isMobile ? 'flex-end' : 'center' }} onClick={cerrarComboDetalle}>
            <div style={{ ...S.detailModal, borderRadius: isMobile ? '20px 20px 0 0' : '20px', maxWidth: '480px', maxHeight: isMobile ? '90vh' : '85vh' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <button onClick={cerrarComboDetalle} style={S.detailClose} aria-label="Cerrar">
                <FiX size={22} />
              </button>

              <div style={S.detailImage}>
                {getImagenUrl(comboDetalle.imagen_url) && !comboImgRota[comboDetalle.id] ? (
                  <img src={getImagenUrl(comboDetalle.imagen_url)} alt={comboDetalle.nombre} style={S.detailImg} onError={() => setComboImgRota((p) => ({ ...p, [comboDetalle.id]: true }))} />
                ) : (
                  <FiPackage size={64} color="var(--color-secondary)" />
                )}
              </div>

              <div style={S.detailBody}>
                <h2 style={S.detailName}>{comboDetalle.nombre}</h2>
                {comboDetalle.descripcion && <p style={S.detailDesc}>{comboDetalle.descripcion}</p>}

                <div style={{ ...S.detailPrices, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={S.detailPriceUsd}>${fmtMonto(totalCombo)}</span>
                  {config?.mostrar_precio_bs !== false && Number.isFinite(tasaCambio) && tasaCambio > 0 && (
                    <span style={S.detailPriceBs}>Bs {fmtMonto(totalCombo * tasaCambio)}</span>
                  )}
                  {comboDetalle.precio_sin_descuento > 0 && comboDetalle.precio_sin_descuento > comboDetalle.precio && (
                    <span style={{ fontSize: '14px', color: 'var(--color-text-secondary)', textDecoration: 'line-through' }}>
                      ${fmtMonto(comboDetalle.precio_sin_descuento)}
                    </span>
                  )}
                  {comboDetalle.ahorro > 0 && (
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#137333', background: '#e6f4ea', padding: '3px 8px', borderRadius: '20px' }}>
                      Ahorras ${fmtMonto(comboDetalle.ahorro)}
                    </span>
                  )}
                </div>

                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '12px 0 8px' }}>
                  Componentes del combo
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                  {instancias.map((it) => {
                    const disponibles = it.etiquetas_disponibles || [];
                    const seleccionados = comboSelEtiquetas[`${it.codigo_producto}#${it._instancia}`] || [];
                    const esMultiple = (parseInt(it.cantidad, 10) || 1) > 1;
                    return (
                      <div key={`${it.codigo_producto}#${it._instancia}`} style={{ borderRadius: '12px', border: '1px solid var(--color-border)', background: 'var(--color-bg)', padding: '12px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-primary)' }}>
                            {esMultiple && <span style={{ color: 'var(--color-secondary)', fontWeight: 700, marginRight: '6px' }}>#{it._instancia + 1}</span>}
                            {it.nombre}
                          </div>
                          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>Incluido</div>
                        </div>
                        {disponibles.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                            {disponibles.map((et) => {
                              const sel = seleccionados.includes(et.id);
                              if (et.incluida) {
                                return (
                                  <span key={et.id} style={{ padding: '5px 10px', borderRadius: '20px', border: '1px solid var(--color-border)', background: 'var(--color-bg-hover)', color: 'var(--color-text-secondary)', fontSize: '11px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ color: 'var(--color-secondary)', fontSize: '10px' }}>✓</span>
                                    {et.nombre} <span style={{ opacity: 0.75 }}>incluido</span>
                                  </span>
                                );
                              }
                              return (
                                <button key={et.id} onClick={() => toggleEtiquetaCombo(it.codigo_producto, it._instancia, et)} style={{ padding: '5px 10px', borderRadius: '20px', border: `1px solid ${sel ? 'var(--color-secondary)' : 'var(--color-border)'}`, background: sel ? 'var(--color-bg-hover)' : 'var(--color-surface)', color: sel ? 'var(--color-secondary)' : 'var(--color-text-secondary)', fontSize: '11px', fontWeight: sel ? 700 : 500, cursor: 'pointer', fontFamily: 'inherit' }}>
                                  {et.nombre}
                                  {et.precio > 0 ? ` (+$${fmtMonto(et.precio)})` : ''}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={S.detailActions}>
                  <button onClick={agregarComboPersonalizado} disabled={agregandoCombo === comboDetalle.id} style={{ ...S.addBtn, opacity: agregandoCombo === comboDetalle.id ? 0.6 : 1 }}>
                    <FiPlus size={18} />
                    {agregandoCombo === comboDetalle.id ? 'Agregando...' : extra > 0 ? `Agregar al pedido · $${fmtMonto(totalCombo)}` : `Agregar al pedido · $${fmtMonto(comboDetalle.precio)}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Carrito sheet */}
      {showCarrito && (
        isMobile ? (
          <>
            <div onClick={() => setShowCarrito(false)} style={S.cartMobileOverlay} />
            <div style={S.cartMobilePanel}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: 'var(--color-primary)' }}>Tu pedido</h3>
                <button onClick={() => setShowCarrito(false)} style={S.detailClose} aria-label="Cerrar">
                  <FiX size={22} />
                </button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                <CartItems carrito={carrito} config={config} tasaCambio={tasaCambio} pva={pva} cambiarCantidad={cambiarCantidad} eliminarDelCarrito={eliminarDelCarrito} />
              </div>
              <div style={{ padding: '16px', borderTop: '1px solid var(--color-border)', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 700, marginBottom: 10 }}>
                  <span>Total</span>
                  <span style={{ color: 'var(--color-secondary)' }}>${fmtMonto(totalCarrito)}</span>
                </div>
                <input
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Tu nombre (opcional)"
                  style={M.nombreInput}
                />
                <button onClick={confirmar} disabled={enviando} style={S.confirmarPedidoBtn}>
                  <FiCheckCircle size={18} /> {enviando ? 'Enviando...' : 'Confirmar pedido'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={S.detailOverlay} onClick={() => setShowCarrito(false)}>
            <div style={{ ...S.detailModal, maxHeight: '80vh' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: 'var(--color-primary)' }}>Tu pedido</h3>
                <button onClick={() => setShowCarrito(false)} style={S.detailClose} aria-label="Cerrar">
                  <FiX size={22} />
                </button>
              </div>
              {carrito.length === 0 ? (
                <p style={{ color: 'var(--color-text-secondary)', textAlign: 'center', padding: '20px' }}>Tu carrito está vacío</p>
              ) : (
                <>
                  <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    <CartItems carrito={carrito} config={config} tasaCambio={tasaCambio} pva={pva} cambiarCantidad={cambiarCantidad} eliminarDelCarrito={eliminarDelCarrito} />
                  </div>
                  <div style={{ borderTop: '1px solid var(--color-border)', padding: '12px 0 0', marginTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 700 }}>
                    <span>Total</span>
                    <span style={{ color: 'var(--color-secondary)' }}>${fmtMonto(totalCarrito)}</span>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <input
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      placeholder="Tu nombre (opcional)"
                      style={M.nombreInput}
                    />
                    <button onClick={confirmar} disabled={enviando} style={S.confirmarPedidoBtn}>
                      <FiCheckCircle size={18} /> {enviando ? 'Enviando...' : 'Confirmar pedido'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )
      )}

      {/* Bottom sheet de categorías - Mobile */}
      {showMobileDeptos && (
        <div onClick={() => setShowMobileDeptos(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, animation: 'fadeIn 0.2s ease' }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'var(--color-surface)', borderRadius: '20px 20px 0 0', maxHeight: '80vh', overflowY: 'auto', zIndex: 2001, animation: 'slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)' }}
          >
            <div style={{ position: 'sticky', top: 0, background: 'var(--color-surface)', padding: '16px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--color-primary)' }}>Categorías</span>
              <button onClick={() => setShowMobileDeptos(false)} style={S.detailClose} aria-label="Cerrar">
                <FiX size={22} />
              </button>
            </div>
            <div style={{ padding: '12px' }}>
              <button
                onClick={() => setMostrarSubgrupos((v) => !v)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  border: '1.5px solid var(--color-border)',
                  backgroundColor: 'var(--color-bg)',
                  color: 'var(--color-secondary)',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  marginBottom: '8px',
                }}
              >
                {mostrarSubgrupos ? 'Ocultar subgrupos' : 'Ver subgrupos'}
                <FiChevronDown size={14} style={{ transform: mostrarSubgrupos ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              <button
                onClick={() => { setDepSeleccionado(null); setGrupoSeleccionado(null); setShowMobileDeptos(false); }}
                style={{ ...SD.sidebarItem, backgroundColor: !depSeleccionado && grupoSeleccionado == null ? 'var(--color-secondary)' : 'transparent', color: !depSeleccionado && grupoSeleccionado == null ? 'var(--color-surface)' : 'var(--color-primary)', fontWeight: !depSeleccionado && grupoSeleccionado == null ? 700 : 500 }}
              >
                <FiGrid size={16} /> Todos los productos
              </button>
              {grupos.map((g, idx) => {
                const color = PALETTE[idx % PALETTE.length];
                const grupoActivo = grupoSeleccionado != null && grupoSeleccionado === g.codigo;
                return (
                  <div key={g.nombre}>
                    <button
                      onClick={() => {
                        setDepSeleccionado(null);
                        seleccionarGrupo(g.codigo);
                        setShowMobileDeptos(false);
                      }}
                      style={{
                        ...SD.sidebarItem,
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        color,
                        backgroundColor: grupoActivo ? `${color}18` : 'transparent',
                        justifyContent: 'space-between',
                        borderLeft: `3px solid ${color}`,
                        paddingLeft: '12px',
                      }}
                    >
                      <span>{g.nombre}</span>
                      <span style={{ fontSize: '10px', fontWeight: 600, background: `${color}18`, color, padding: '1px 7px', borderRadius: '10px' }}>
                        {g.depts.length}
                      </span>
                    </button>
                    {mostrarSubgrupos &&
                      g.depts.map((d) => {
                        const sel = depSeleccionado === d.codigo;
                        return (
                          <button
                            key={d.codigo}
                            onClick={() => { seleccionarDepartamento(d.codigo); setGrupoSeleccionado(null); setShowMobileDeptos(false); }}
                            style={{ ...SD.sidebarItem, backgroundColor: sel ? 'var(--color-bg-hover)' : 'transparent', color: sel ? 'var(--color-secondary)' : 'var(--color-text)', fontWeight: sel ? 600 : 400 }}
                          >
                            {d.nombre}
                          </button>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {visorOpen && (
        <VisorImagen images={visorOpen.images} startIdx={visorOpen.startIdx || 0} onClose={() => setVisorOpen(null)} />
      )}
    </div>
  );
}

function CartItems({ carrito, config, tasaCambio, pva, cambiarCantidad, eliminarDelCarrito }) {
  return (
    <>
      {carrito.map((c) => (
        <div key={c._key} style={S.cartItemRow}>
          <div style={{ width: 40, height: 40, borderRadius: 8, flexShrink: 0, overflow: 'hidden', backgroundColor: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {getImagenUrl(c.imagen_url) ? (
              <img src={getImagenUrl(c.imagen_url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => (e.currentTarget.style.display = 'none')} />
            ) : (
              <FiPackage size={16} color="var(--color-text-muted)" />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-primary)' }}>{c.nombre}</div>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
              ${fmtMonto(c.precio || 0)}{config?.mostrar_precio_bs !== false ? ` / Bs ${fmtMonto((c.precio || 0) * (tasaCambio || 0))}` : ''}
            </div>
            {c.pesable && (
              <div style={{ fontSize: '10px', color: '#b06000' }}>Pesable - {(c.cantidad / 1000).toFixed(2)} kg</div>
            )}
            {c.etiquetas?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                {c.etiquetas.map((et) => (
                  <span key={et.id} style={{ fontSize: '10px', fontWeight: 500, padding: '2px 6px', borderRadius: '6px', backgroundColor: 'var(--color-bg)', color: 'var(--color-text-secondary)' }}>
                    {et.nombre}{et.precio > 0 ? ` +$${fmtMonto(et.precio)}` : ''}
                  </span>
                ))}
              </div>
            )}
            {c.combo_id && c.componentes?.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '5px' }}>
                {c.componentes.map((comp, ci) => (
                  <div key={ci} style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                    {comp.nombre}
                    {comp.etiquetas?.length > 0 && (
                      <span style={{ color: 'var(--color-text-muted)' }}>{' · '}{comp.etiquetas.map((e) => e.nombre).join(', ')}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button onClick={() => cambiarCantidad(c._key, c.pesable ? -50 : -1)} disabled={c.cantidad <= (c.pesable ? 50 : 1)} style={{ ...S.cartCantBtn, opacity: c.cantidad <= (c.pesable ? 50 : 1) ? 0.3 : 1 }} aria-label="Disminuir cantidad">
              <FiMinus size={12} />
            </button>
            <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '40px', textAlign: 'center' }}>
              {c.pesable ? (c.cantidad / 1000).toFixed(2) + ' kg' : c.cantidad}
            </span>
            <button onClick={() => cambiarCantidad(c._key, c.pesable ? 50 : 1)} disabled={!pva && !c.combo_id && c.cantidad >= (c.pesable ? (c.stock || 0) * 1000 : (c.stock || 0))} style={{ ...S.cartCantBtn, opacity: !pva && !c.combo_id && c.cantidad >= (c.pesable ? (c.stock || 0) * 1000 : (c.stock || 0)) ? 0.3 : 1 }} aria-label="Aumentar cantidad">
              <FiPlus size={12} />
            </button>
            <button onClick={() => eliminarDelCarrito(c._key)} style={{ ...S.cartCantBtn, color: 'var(--color-danger)', borderColor: '#FFCDD2' }} aria-label="Eliminar">
              <FiX size={12} />
            </button>
          </div>
        </div>
      ))}
    </>
  );
}

const SC = {
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
    backgroundColor: 'var(--color-bg)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    maxWidth: 1100,
    margin: '0 auto',
    width: '100%',
    overflow: 'hidden',
  },
  header: {
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  logo: { width: 40, height: 40, borderRadius: 10, objectFit: 'contain', flexShrink: 0 },
  cartBtn: {
    position: 'relative',
    background: 'rgba(255,255,255,0.18)',
    border: 'none',
    color: 'var(--color-surface)',
    borderRadius: 12,
    width: 42,
    height: 42,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  errorBanner: {
    backgroundColor: 'var(--color-danger-light)',
    color: 'var(--color-danger)',
    padding: '10px 16px',
    fontSize: 13,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  center: {
    minHeight: '100dvh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-bg)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
};

MesaPedido.displayName = 'MesaPedido';

export default MesaPedido;
